import { resolveMediaUrl } from "@/lib/editorialMedia.mjs";
import { useEditorialMediaUrl } from "@/lib/useEditorialMediaUrl";
import { editorialImageStyle, hasExplicitEditorialCrop } from "@/lib/editorialReaderLayout.mjs";

const BG = {
  "warm-ivory": "#EFE6D5",
  "mineral-plaster": "#E6DFCF",
  "almond-paper": "#F1E7D2",
  "deep-cocoa": "#2A1810",
  "full-image": "#EFE6D5",
};

const PALETTE = {
  ivory: "#EFE6D5",
  almond: "#D6B98A",
  olive: "#7E8A3E",
  cocoa: "#5B3B25",
  burgundy: "#7A2A2A",
  gold: "#C9A84C",
  pistachio: "#A6C088",
  apricot: "#E8B48A",
};

const serif = "'Cormorant Garamond', 'Playfair Display', serif";

function paletteColor(key) {
  return PALETTE[key] || PALETTE.olive;
}

function backgroundColor(key) {
  return BG[key] || BG["warm-ivory"];
}

function mediaStyle(media) {
  return editorialImageStyle(media || {});
}

function naturalMedia({ src, media, alt = "", maxDesktop = 620, maxMobile = 420, className = "" }) {
  if (!src) return null;
  const cropped = hasExplicitEditorialCrop(media || {});
  return (
    <div className={`relative flex w-full items-center justify-center overflow-hidden ${className}`.trim()}>
      <img
        src={src}
        alt={alt}
        className={cropped ? "h-full w-full object-cover" : "block h-auto w-auto max-w-full object-contain"}
        style={{
          ...mediaStyle(media),
          maxHeight: cropped ? undefined : `min(${maxDesktop}px, 72vh)`,
        }}
      />
      <style>{`@media (max-width: 767px){[data-editorial-natural-media] img{max-height:${maxMobile}px !important;}}`}</style>
    </div>
  );
}

function heroImage({ src, media, alt = "", mobile = false, fullBleed = false }) {
  if (!src) return null;
  if (fullBleed) {
    return (
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        style={mediaStyle(media)}
      />
    );
  }
  return (
    <div data-editorial-natural-media className="flex h-full w-full items-center justify-center overflow-hidden">
      {naturalMedia({ src, media, alt, maxDesktop: 650, maxMobile: 520 })}
    </div>
  );
}

export function EditorialBaselineHeroRender({ block, mode = "desktop" }) {
  const c = block?.content || {};
  const s = block?.settings || {};
  const variant = s.variant || "A";
  const fullBleed = variant === "C" || s.background === "full-image";
  const bg = backgroundColor(s.background);
  const dark = s.background === "deep-cocoa" || fullBleed;
  const text = dark ? "#F1E4CB" : "#3B241A";
  const accent = paletteColor(s.palette);
  const desktopSrc = useEditorialMediaUrl(c.hero_url, c.hero_media_id);
  const mobileOwn = useEditorialMediaUrl(c.hero_mobile_url, c.hero_mobile_media_id);
  const mobileSrc = mobileOwn || desktopSrc;
  const desktopMedia = { focal_x: s.focal_x ?? 0.5, focal_y: s.focal_y ?? 0.5, zoom: s.hero_zoom || 1 };
  const mobileMedia = {
    focal_x: s.mobile_focal_x ?? s.focal_x ?? 0.5,
    focal_y: s.mobile_focal_y ?? s.focal_y ?? 0.5,
    zoom: s.hero_mobile_zoom || 1,
  };

  if (mode === "mobile") {
    if (fullBleed && mobileSrc) {
      return (
        <section className="relative min-h-[720px] w-full overflow-hidden bg-[#2A1810]" data-testid="editorial-baseline-hero-mobile">
          {heroImage({ src: mobileSrc, media: mobileMedia, alt: c.alt, mobile: true, fullBleed: true })}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/10" />
          <div className="absolute inset-x-0 bottom-0 px-7 pb-10 pt-28" style={{ color: "#F6EAD7" }}>
            {c.eyebrow && <div className="mb-5 text-[9px] uppercase tracking-[0.42em] opacity-80">{c.eyebrow}</div>}
            <h1 className="max-w-[10ch] font-serif font-normal leading-[0.94]" style={{ fontFamily: serif, fontSize: "clamp(42px, 13vw, 58px)" }}>{c.headline || ""}</h1>
            <div className="mt-4 flex items-center gap-2"><span className="h-px w-9" style={{ background: accent }} /><span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /></div>
            {c.subtitle && <div className="mt-4 max-w-[28ch] text-[17px] italic leading-[1.45]">{c.subtitle}</div>}
            {c.lead && <p className="mt-5 max-w-[30ch] text-[14px] leading-[1.7] opacity-92">{c.lead}</p>}
          </div>
        </section>
      );
    }

    return (
      <section className="w-full overflow-hidden" style={{ background: bg, color: text }} data-testid="editorial-baseline-hero-mobile">
        {mobileSrc && <div className="w-full px-5 pt-5">{heroImage({ src: mobileSrc, media: mobileMedia, alt: c.alt, mobile: true })}</div>}
        <div className="px-7 pb-10 pt-7">
          {c.eyebrow && <div className="mb-4 text-[9px] uppercase tracking-[0.38em] opacity-70">{c.eyebrow}</div>}
          <h1 className="max-w-[10ch] font-serif font-normal leading-[0.96]" style={{ fontFamily: serif, fontSize: "clamp(38px, 11vw, 50px)" }}>{c.headline || ""}</h1>
          <div className="mt-4 flex items-center gap-2"><span className="h-px w-9" style={{ background: accent }} /><span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /></div>
          {c.subtitle && <div className="mt-4 max-w-[30ch] text-[16px] italic leading-[1.45] opacity-85">{c.subtitle}</div>}
          {c.lead && <p className="mt-5 max-w-[32ch] text-[14px] leading-[1.75] opacity-90">{c.lead}</p>}
          {c.caption && <div className="mt-5 text-[10px] italic opacity-55">{c.caption}</div>}
        </div>
      </section>
    );
  }

  if (fullBleed && desktopSrc) {
    return (
      <section className="relative mx-auto min-h-[640px] w-full max-w-[1180px] overflow-hidden bg-[#2A1810]" data-testid="editorial-baseline-hero-desktop">
        {heroImage({ src: desktopSrc, media: desktopMedia, alt: c.alt, fullBleed: true })}
        <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/15 to-black/72" />
        <div className="absolute inset-y-0 right-0 flex w-[46%] items-center px-12 py-14" style={{ color: "#F6EAD7" }}>
          <div className="max-w-[430px]">
            {c.eyebrow && <div className="mb-6 text-[10px] uppercase tracking-[0.46em] opacity-80">{c.eyebrow}</div>}
            <h1 className="font-serif font-normal leading-[0.92]" style={{ fontFamily: serif, fontSize: "clamp(58px, 6vw, 90px)" }}>{c.headline || ""}</h1>
            <div className="mt-5 flex items-center gap-2"><span className="h-px w-10" style={{ background: accent }} /><span className="h-2 w-2 rounded-full" style={{ background: accent }} /></div>
            {c.subtitle && <div className="mt-5 text-[19px] italic leading-[1.45]">{c.subtitle}</div>}
            {c.lead && <p className="mt-7 max-w-[34ch] text-[15px] leading-[1.8] opacity-92">{c.lead}</p>}
          </div>
        </div>
      </section>
    );
  }

  const image = desktopSrc ? <div className="min-h-0">{heroImage({ src: desktopSrc, media: desktopMedia, alt: c.alt })}</div> : null;
  const copy = (
    <div className="flex min-w-0 items-center px-6 py-10 lg:px-12 lg:py-14" style={{ color: text }}>
      <div className="max-w-[430px]">
        {c.eyebrow && <div className="mb-5 text-[10px] uppercase tracking-[0.42em] opacity-72">{c.eyebrow}</div>}
        <h1 className="font-serif font-normal leading-[0.96] [overflow-wrap:anywhere]" style={{ fontFamily: serif, fontSize: "clamp(50px, 5vw, 72px)" }}>{c.headline || ""}</h1>
        <div className="mt-5 flex items-center gap-2"><span className="h-px w-10" style={{ background: accent }} /><span className="h-2 w-2 rounded-full" style={{ background: accent }} /></div>
        {c.subtitle && <div className="mt-5 max-w-[32ch] text-[18px] italic leading-[1.45] opacity-86">{c.subtitle}</div>}
        {c.lead && <p className="mt-6 max-w-[38ch] text-[15px] leading-[1.78] opacity-90">{c.lead}</p>}
        {c.caption && <div className="mt-6 text-[10px] italic opacity-55">{c.caption}</div>}
      </div>
    </div>
  );

  return (
    <section className="mx-auto w-full max-w-[1180px] overflow-hidden" style={{ background: bg }} data-testid="editorial-baseline-hero-desktop">
      <div className="grid min-h-[610px] items-stretch lg:grid-cols-2">
        {variant === "B" ? <>{copy}{image}</> : <>{image}{copy}</>}
      </div>
    </section>
  );
}

export function EditorialBaselineStoryRender({ block, mode = "desktop" }) {
  const c = block?.content || {};
  const s = block?.settings || {};
  const variant = s.variant || "A";
  const bg = backgroundColor(s.background);
  const dark = s.background === "deep-cocoa";
  const text = dark ? "#F1E4CB" : "#3B241A";
  const accent = paletteColor(s.palette);
  const paragraphs = Array.isArray(c.extra?.paragraphs) ? c.extra.paragraphs.filter(Boolean) : [];
  const desktopSrc = useEditorialMediaUrl(c.hero_url, c.hero_media_id);
  const mobileOwn = useEditorialMediaUrl(c.hero_mobile_url, c.hero_mobile_media_id);
  const mobileSrc = mobileOwn || desktopSrc;
  const desktopMedia = { focal_x: s.focal_x ?? 0.5, focal_y: s.focal_y ?? 0.5, zoom: s.hero_zoom || 1 };
  const mobileMedia = {
    focal_x: s.mobile_focal_x ?? s.focal_x ?? 0.5,
    focal_y: s.mobile_focal_y ?? s.focal_y ?? 0.5,
    zoom: s.hero_mobile_zoom || 1,
  };

  const storyCopy = (mobile = false) => (
    <div className="min-w-0" style={{ color: text }}>
      {c.eyebrow && <div className="mb-4 text-[9px] uppercase tracking-[0.38em]" style={{ color: accent }}>{c.eyebrow}</div>}
      <h1 className="font-serif font-normal leading-[1.0] [overflow-wrap:anywhere]" style={{ fontFamily: serif, fontSize: mobile ? "clamp(34px, 10vw, 44px)" : "clamp(50px, 5vw, 72px)" }}>{c.headline || ""}</h1>
      {c.subtitle && <div className="mt-4 max-w-[38ch] text-[17px] italic leading-[1.45] opacity-82">{c.subtitle}</div>}
      <div className="mt-5 flex items-center gap-2"><span className="h-px w-10" style={{ background: accent }} /><span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} /></div>
      {c.lead && <p className={`mt-6 max-w-[42ch] font-serif italic ${mobile ? "text-[17px]" : "text-[22px]"} leading-[1.48]`}>{c.lead}</p>}
      {paragraphs.length > 0 && <div className={`mt-7 max-w-[56ch] space-y-5 ${mobile ? "text-[14px]" : "text-[15px]"} leading-[1.82]`}>{paragraphs.map((p, i) => <p key={i}>{p}</p>)}</div>}
      {c.quote && <blockquote className={`mt-8 max-w-[46ch] border-l pl-5 font-serif italic ${mobile ? "text-[18px]" : "text-[22px]"} leading-[1.45]`} style={{ borderColor: accent }}>{c.quote}</blockquote>}
      {c.caption && <div className="mt-5 text-[10px] italic opacity-55">{c.caption}</div>}
    </div>
  );

  if (mode === "mobile") {
    return (
      <section className="w-full overflow-hidden px-6 py-8" style={{ background: bg }} data-testid="editorial-baseline-story-mobile">
        {mobileSrc && <div className="mb-7">{naturalMedia({ src: mobileSrc, media: mobileMedia, alt: c.alt, maxDesktop: 520, maxMobile: 520 })}</div>}
        {storyCopy(true)}
      </section>
    );
  }

  const image = desktopSrc ? <div className="flex h-full items-center justify-center">{naturalMedia({ src: desktopSrc, media: desktopMedia, alt: c.alt, maxDesktop: 760, maxMobile: 520 })}</div> : null;
  const copy = <div className="px-8 py-12 lg:px-12 lg:py-14">{storyCopy(false)}</div>;

  return (
    <section className="mx-auto w-full max-w-[1180px] overflow-hidden" style={{ background: bg }} data-testid="editorial-baseline-story-desktop">
      <div className={`grid min-h-[650px] items-stretch ${desktopSrc ? "lg:grid-cols-2" : ""}`}>
        {desktopSrc ? (variant === "B" ? <>{copy}{image}</> : <>{image}{copy}</>) : <div className="mx-auto w-full max-w-[760px]">{copy}</div>}
      </div>
    </section>
  );
}

function readMacro(content) {
  const c = content || {};
  const m = c.macro_sensory && typeof c.macro_sensory === "object" ? c.macro_sensory : {};
  const readImg = (img) => ({
    media_id: img?.media_id ?? null,
    url: img?.url ?? null,
    alt: img?.alt ?? "",
    focal_x: img?.focal_x ?? 0.5,
    focal_y: img?.focal_y ?? 0.5,
    zoom: img?.zoom ?? 1,
  });
  return {
    eyebrow: m.eyebrow ?? "",
    title: m.title ?? c.headline ?? "",
    subtitle: m.subtitle ?? c.lead ?? "",
    text_blocks: Array.isArray(m.text_blocks) ? m.text_blocks.filter((b) => b?.text?.trim()) : [],
    sensory_notes: Array.isArray(m.sensory_notes) ? m.sensory_notes.filter((n) => n?.label?.trim() || n?.text?.trim()) : [],
    accent_phrase: m.accent_phrase ?? "",
    caption: m.caption ?? c.caption ?? "",
    main_image: readImg(m.main_image),
    mobile_image: readImg(m.mobile_image),
  };
}

function MacroNotes({ notes }) {
  if (!notes.length) return null;
  return (
    <div className="mt-7">
      <div className="mb-2 border-b border-[#3B241A]/20 pb-2 text-[9px] uppercase tracking-[0.32em] text-[#6F5639]">Сенсорные ноты</div>
      <ul className="divide-y divide-[#3B241A]/12">
        {notes.map((n, i) => (
          <li key={n.id || i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4 py-3">
            <div className="text-[9px] uppercase tracking-[0.24em] text-[#8A6A46]">{n.label}</div>
            <div className="font-serif text-[15px] leading-[1.55] text-[#2A1D14]">{n.text}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EditorialBaselineMacroSensoryRender({ block, mode = "desktop" }) {
  const m = readMacro(block?.content);
  const desktopSrc = useEditorialMediaUrl(m.main_image.url, m.main_image.media_id);
  const mobileOwn = useEditorialMediaUrl(m.mobile_image.url, m.mobile_image.media_id);
  const mobile = mode === "mobile";
  const src = mobileOwn || desktopSrc;
  const img = mobileOwn ? m.mobile_image : m.main_image;

  if (mobile) {
    return (
      <section className="w-full overflow-hidden bg-[#EFE6D5] px-6 py-8" data-testid="editorial-baseline-macro-mobile">
        {m.eyebrow && <div className="mb-3 text-[9px] uppercase tracking-[0.32em] text-[#8A6A46]">{m.eyebrow}</div>}
        {m.title && <h2 className="font-serif text-[34px] font-normal leading-[1.0] text-[#2A1D14]" style={{ fontFamily: serif }}>{m.title}</h2>}
        {m.subtitle && <p className="mt-4 font-serif text-[16px] italic leading-[1.5] text-[#5B4326]">{m.subtitle}</p>}
        {src && <div className="mt-6">{naturalMedia({ src, media: img, alt: img.alt, maxDesktop: 520, maxMobile: 520 })}</div>}
        {m.caption && <div className="mt-3 text-[10px] italic text-[#5B4326] opacity-65">{m.caption}</div>}
        {m.text_blocks.length > 0 && <div className="mt-6 space-y-4 text-[14px] leading-[1.78] text-[#2A1D14]">{m.text_blocks.map((b, i) => <p key={b.id || i}>{b.text}</p>)}</div>}
        <MacroNotes notes={m.sensory_notes} />
        {m.accent_phrase && <p className="mt-7 border-l border-[#A67B4A] pl-4 font-serif text-[17px] italic leading-[1.45] text-[#3E2E20]">{m.accent_phrase}</p>}
      </section>
    );
  }

  return (
    <section className="w-full overflow-hidden bg-[#EFE6D5]" data-testid="editorial-baseline-macro-desktop">
      <div className={`mx-auto grid min-h-[620px] w-full max-w-[1180px] items-center gap-10 px-8 py-10 lg:px-10 lg:py-12 ${src ? "lg:grid-cols-[46%_54%]" : ""}`}>
        {src && <div>{naturalMedia({ src, media: img, alt: img.alt, maxDesktop: 620, maxMobile: 520 })}{m.caption && <div className="mt-3 text-[10px] italic text-[#5B4326] opacity-65">{m.caption}</div>}</div>}
        <div className="min-w-0">
          {m.eyebrow && <div className="mb-4 text-[9px] uppercase tracking-[0.34em] text-[#8A6A46]">{m.eyebrow}</div>}
          {m.title && <h2 className="font-serif font-normal leading-[1.0] text-[#2A1D14]" style={{ fontFamily: serif, fontSize: "clamp(42px, 4vw, 60px)" }}>{m.title}</h2>}
          {m.subtitle && <p className="mt-4 max-w-[42ch] font-serif text-[18px] italic leading-[1.5] text-[#5B4326]">{m.subtitle}</p>}
          {m.text_blocks.length > 0 && <div className="mt-6 max-w-[56ch] space-y-4 text-[15px] leading-[1.78] text-[#2A1D14]">{m.text_blocks.map((b, i) => <p key={b.id || i}>{b.text}</p>)}</div>}
          <MacroNotes notes={m.sensory_notes} />
          {m.accent_phrase && <p className="mt-7 max-w-[44ch] border-l border-[#A67B4A] pl-5 font-serif text-[19px] italic leading-[1.45] text-[#3E2E20]">{m.accent_phrase}</p>}
        </div>
      </div>
    </section>
  );
}
